import { ComponentConfig, WithPuckProps } from '@measured/puck';
import { Row } from '@tanstack/react-table';
import { DownloadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../components/atoms/button/button';
import { Table } from '../../../../../components/molecules/table/table';
import { ColumnDef } from '../../../../../components/molecules/table/types';
import { useFormatCellAsDate } from '../../../../../utils/use-format-cell-as-date';
import { useReportContext } from '../../../renderer/report-context';
import { getReportMetadata, ReportAttachment } from '../../../report-types';

const BYTES_PER_UNIT = 1024;
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

const formatFileSize = (bytes: number): string => {
  if (bytes <= 0) return '0 B';

  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT)), FILE_SIZE_UNITS.length - 1);
  const value = bytes / BYTES_PER_UNIT ** exponent;

  return `${exponent === 0 ? value : value.toFixed(1)} ${FILE_SIZE_UNITS[exponent]}`;
};

const DownloadAttachmentCell = ({ attachment }: { readonly attachment: ReportAttachment }) => {
  const { downloadAttachment } = useReportContext();

  return (
    <Button
      appearance="secondary"
      aria-label="Download attachment"
      icon={DownloadIcon}
      onClick={() => downloadAttachment(attachment)}
      size="sm"
    />
  );
};

type AttachmentsBlockProps = Record<never, never>;

const ActionsCell = ({ row }: { readonly row: Row<ReportAttachment> }) => (
  <DownloadAttachmentCell attachment={row.original} />
);

const AttachmentsBlockRender = ({ puck }: WithPuckProps<AttachmentsBlockProps>) => {
  const { t } = useTranslation();
  const { attachments = [], isPdf } = getReportMetadata(puck.metadata) ?? {};
  const dateFormatter = useFormatCellAsDate<ReportAttachment>(t, 'datetime.short');

  const columns: ColumnDef<ReportAttachment>[] = [
    { accessorKey: 'fileName', enableSorting: false, header: t('report.blocks.attachments.columnHeaderFileName') },
    {
      accessorKey: 'contentType',
      enableSorting: false,
      header: t('report.blocks.attachments.columnHeaderContentType'),
    },
    {
      accessorKey: 'sizeBytes',
      cell: ({ getValue }) => formatFileSize(getValue<number>()),
      enableSorting: false,
      header: t('report.blocks.attachments.columnHeaderFileSize'),
    },
    {
      accessorKey: 'createdAt',
      cell: dateFormatter,
      enableSorting: false,
      header: t('report.blocks.attachments.columnHeaderCreatedAt'),
    },
    {
      accessorKey: 'respondentType',
      enableSorting: false,
      header: t('report.blocks.attachments.columnHeaderRespondentType'),
    },
    ...(isPdf ? [] : [{ cell: ActionsCell, enableSorting: false, header: t('common.table.actions'), id: 'download' }]),
  ];

  return <Table columns={columns} data={attachments} pageSize={null} />;
};

const AttachmentsBlock: ComponentConfig<AttachmentsBlockProps> = {
  label: 'Attachments',
  fields: {},
  defaultProps: {},
  render: AttachmentsBlockRender,
};

export { AttachmentsBlock };
